import React, { useEffect, useRef, useState } from "react";
import EmojiPicker from "emoji-picker-react";
import { useCookies } from "react-cookie";
import { Icon } from "../../../components/Component";
import { serviceGetQuickReplyChat } from "../../../services/quickReply";
import LivechatAttachmentPreview from "./LivechatAttachmentPreview";
import { kindOf } from "./livechatUpload";

const QUICK_REPLY_DELAY_MS = 250;
const MAX_TEXT = 4000;

const quoteOf = (message) => message.text || `[${message.type}]`;

// the WhatsApp chat's upload menu: one picker per kind
const UPLOAD_OPTIONS = [
  { kind: "image", icon: "img-fill", label: "Kirim gambar" },
  { kind: "file", icon: "file-fill", label: "Kirim dokumen" },
  { kind: "video", icon: "video-fill", label: "Kirim video" },
];

const LivechatComposer = ({
  departmentId,
  isDisabled,
  onReopen,
  replyTo,
  onCancelReply,
  onSend,
  onTyping,
  accept,
  onSendFile,
}) => {
  const [cookies] = useCookies(["SIDikey"]);
  const [text, setText] = useState("");
  const [isEmojiOpen, setIsEmojiOpen] = useState(false);
  const [quickReplies, setQuickReplies] = useState([]);
  const textareaRef = useRef(null);
  const debounceRef = useRef(null);
  const fileRefs = useRef({});
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [attachment, setAttachment] = useState(null);

  const pick = (file) => {
    setIsUploadOpen(false);
    if (file) setAttachment({ file, kind: kindOf(file) });
  };

  // a pasted file opens the preview, as on the WhatsApp chat; text pastes as usual
  const onPaste = (e) => {
    const [file] = e.clipboardData?.files ?? [];
    if (!file || !onSendFile) return;

    e.preventDefault();
    pick(file);
  };

  const command = text.startsWith("/") ? text.slice(1) : null;

  useEffect(() => {
    clearTimeout(debounceRef.current);

    if (command === null || command.includes(" ")) {
      setQuickReplies([]);
      return undefined;
    }

    let isStale = false;
    debounceRef.current = setTimeout(async () => {
      const res = await serviceGetQuickReplyChat(cookies.SIDikey, command, departmentId ? [departmentId] : []);
      if (!isStale) setQuickReplies(Array.isArray(res?.data) ? res.data : []);
    }, QUICK_REPLY_DELAY_MS);

    return () => {
      isStale = true;
      clearTimeout(debounceRef.current);
    };
  }, [command, cookies.SIDikey, departmentId]);

  useEffect(() => {
    if (replyTo) textareaRef.current?.focus();
  }, [replyTo]);

  const submit = () => {
    const message = text.trim();

    if (!message || isDisabled) return;

    onSend(message, replyTo);
    setText("");
    setIsEmojiOpen(false);
  };

  const onKeyDown = (e) => {
    // Shift+Enter keeps a new line; IME composition must finish first
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      submit();
    }
  };

  const pickQuickReply = (reply) => {
    setText(reply.response ?? "");
    setQuickReplies([]);
    textareaRef.current?.focus();
  };

  if (isDisabled) {
    return (
      <div className="nk-chat-editor justify-content-center align-items-center" style={{ gap: 12 }}>
        <span className="sub-text">Menunggu pengunjung menulis</span>
        {onReopen && (
          <button type="button" className="btn btn-sm btn-round btn-outline-success" onClick={onReopen}>
            Buka kembali
          </button>
        )}
      </div>
    );
  }

  return (
    <>
      <LivechatAttachmentPreview
        attachment={attachment}
        onClose={() => setAttachment(null)}
        onSend={(file, caption) => {
          setAttachment(null);
          onSendFile(file, caption);
        }}
      />
      <div className="position-relative">
        {quickReplies.length > 0 && (
          <ul className="lc-quick-replies" role="listbox" aria-label="Quick reply">
            {quickReplies.map((reply) => (
              <li key={reply.id ?? reply.command}>
                <button type="button" className="lc-quick-reply" onClick={() => pickQuickReply(reply)}>
                  <strong>/{reply.command}</strong>
                  <span className="text-truncate">{reply.response}</span>
                </button>
              </li>
            ))}
          </ul>
        )}

        {replyTo && (
          <div className="lc-replying">
            <div className="text-truncate">
              <span className="lead-text">Membalas</span>
              <span className="sub-text text-truncate">{quoteOf(replyTo)}</span>
            </div>
            <button type="button" className="btn btn-icon btn-trigger" aria-label="Batal membalas" onClick={onCancelReply}>
              <Icon name="cross" />
            </button>
          </div>
        )}

        <div className="nk-chat-editor">
          <div className="nk-chat-editor-upload ml-n1">
            {onSendFile && (
              <>
                <div className={`chat-upload-option ${isUploadOpen ? "expanded" : ""}`}>
                  <ul>
                    {UPLOAD_OPTIONS.filter(({ kind }) => accept?.[kind]).map(({ kind, icon, label }) => (
                      <li key={kind}>
                        <button
                          type="button"
                          className="btn btn-icon btn-trigger text-primary"
                          aria-label={label}
                          onClick={() => fileRefs.current[kind]?.click()}
                        >
                          <Icon name={icon} />
                        </button>
                        <input
                          ref={(input) => {
                            fileRefs.current[kind] = input;
                          }}
                          hidden
                          type="file"
                          accept={accept[kind]}
                          aria-label={label}
                          onChange={(e) => {
                            const [file] = e.target.files;
                            e.target.value = "";
                            pick(file);
                          }}
                        />
                      </li>
                    ))}
                  </ul>
                </div>
                <button
                  type="button"
                  className={`btn btn-icon btn-trigger text-primary toggle-opt ${isUploadOpen ? "active" : ""}`}
                  aria-label="Lampirkan file"
                  aria-expanded={isUploadOpen}
                  onClick={() => setIsUploadOpen((open) => !open)}
                >
                  <Icon name="plus-circle-fill" />
                </button>
              </>
            )}
            <button
              type="button"
              className="btn btn-icon btn-trigger text-primary"
              aria-label={isEmojiOpen ? "Tutup emoji" : "Pilih emoji"}
              aria-pressed={isEmojiOpen}
              onClick={() => setIsEmojiOpen((open) => !open)}
            >
              <Icon name={isEmojiOpen ? "cross" : "happyf-fill"} />
            </button>
            {isEmojiOpen && (
              <div className="emoji-picker">
                <EmojiPicker
                  emojiStyle="google"
                  onEmojiClick={(data) => {
                    setText((current) => current + data.emoji);
                    textareaRef.current?.focus();
                  }}
                />
              </div>
            )}
          </div>
          <div className="nk-chat-editor-form">
            <div className="form-control-wrap">
              <textarea
                ref={textareaRef}
                className="form-control form-control-simple no-resize"
                rows="1"
                placeholder="Tulis pesan, / untuk quick reply"
                aria-label="Tulis pesan"
                maxLength={MAX_TEXT}
                value={text}
                onChange={(e) => {
                  setText(e.target.value);
                  if (e.target.value.trim()) onTyping?.();
                }}
                onKeyDown={onKeyDown}
                onPaste={onPaste}
              />
            </div>
          </div>
          <ul className="nk-chat-editor-tools g-2">
            <li>
              <button
                type="button"
                className="btn btn-round btn-primary btn-icon"
                aria-label="Kirim"
                disabled={!text.trim()}
                onClick={submit}
              >
                <Icon name="send-alt" />
              </button>
            </li>
          </ul>
        </div>
      </div>
    </>
  );
};

export default LivechatComposer;
